import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import adminApi from "../../../api/adminApi";
import "../../../assets/css/admin/notifications.css";

const timeAgo = (dateString) => {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return new Date(dateString).toLocaleDateString();
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchNotifications = async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/notifications/all?page=${page}`);
      setNotifications(res.data.notifications || []);
      setCurrentPage(res.data.current_page || 1);
      setLastPage(res.data.last_page || 1);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
  }, []);

  const markAsRead = async (id) => {
    try {
      await adminApi.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  };

  const clearAll = async () => {
    const result = await Swal.fire({
      title: "Clear all notifications?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, clear all",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await adminApi.delete("/notifications/clear-all");
      setNotifications([]);
    } catch (err) {
      console.error("Failed to clear notifications", err);
    }
  };

  const deleteOne = async (id) => {
    try {
      await adminApi.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Failed to delete notification", err);
    }
  };

  const unreadTotal = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="admin-notifications-page">
      {/* Header */}
      <div className="admin-notifications-header">
        <div>
          <h1>Notifications</h1>
          <p>System alerts, AI warnings, payments and support updates</p>
        </div>

        <div className="admin-header-actions">
          <button
            className="admin-btn-secondary"
            onClick={clearAll}
            disabled={notifications.length === 0}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-notifications-stats">
        <div className="admin-notification-stat-card">
          <h3>Total</h3>
          <span>{notifications.length}</span>
        </div>
        <div className="admin-notification-stat-card unread">
          <h3>Unread</h3>
          <span>{unreadTotal}</span>
        </div>
      </div>

      {/* List */}
      <div className="admin-notifications-list">
        {loading ? (
          <div className="admin-empty-state">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="admin-empty-state">No notifications available</div>
        ) : (
          notifications.map((item) => (
            <div
              key={item.id}
              className={`admin-notification-card ${item.is_read ? "read" : "unread"}`}
            >
              <div className="admin-notification-left">
                <div className={`admin-type-badge ${item.type || "general"}`}>
                  {(item.type || "general").toUpperCase()}
                </div>

                <div className="admin-notification-content">
                  <h4>{item.title}</h4>
                  <p>{item.message}</p>
                </div>
              </div>

              <div className="admin-notification-right">
                <span className="admin-notif-time">{timeAgo(item.created_at)}</span>

                {!item.is_read && (
                  <button className="admin-mark-btn" onClick={() => markAsRead(item.id)}>
                    Mark as read
                  </button>
                )}
                <button className="admin-mark-btn" onClick={() => deleteOne(item.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && lastPage > 1 && (
        <div className="admin-notifications-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => fetchNotifications(currentPage - 1)}
          >
            Prev
          </button>
          <span>
            Page {currentPage} of {lastPage}
          </span>
          <button
            disabled={currentPage === lastPage}
            onClick={() => fetchNotifications(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Notifications;